/**
 * The FlashBook studio's autosave: the shared studio autosave, told how to build
 * and store a *book*.
 *
 * The rules — the debounce, the flush on leaving and on backgrounding, and the two
 * that keep a session from destroying the copy it is about to offer back — live in
 * `#/components/studio/useStudioAutosave`, because the Capsule studio needs
 * exactly the same behaviour and two copies of rules this subtle would be two that
 * drift. What is left here is the part that is actually about books: the snapshot's
 * shape, and the slot it goes in.
 */
import {
  clearFlashBookSnapshot,
  createFlashBookSnapshot,
  type FlashBookRecoveredBook,
  putFlashBookSnapshot,
} from '#/lib/flashbookRecovery'
import {
  type AutosaveState,
  type StudioAutosave,
  useStudioAutosave,
} from '#/components/studio/useStudioAutosave'

export type {AutosaveState}

export type FlashBookAutosave = StudioAutosave

export function useFlashBookAutosave({
  signature,
  keep,
  draftUri,
  take,
  delayMs,
}: {
  /** The book's content fingerprint — the thing that changes when it changes. */
  signature: string
  /** Whether this session has work worth keeping (see the shared hook). */
  keep: boolean
  /** The draft record this work came from, so a copy can be given back later. */
  draftUri: string | null
  /** Reads the book, called at write time. */
  take: () => FlashBookRecoveredBook
  delayMs?: number
}): FlashBookAutosave {
  return useStudioAutosave({
    signature,
    keep,
    take: savedAt =>
      createFlashBookSnapshot({draftUri, signature, book: take(), now: savedAt}),
    write: putFlashBookSnapshot,
    clear: clearFlashBookSnapshot,
    delayMs,
  })
}
